import { Link } from '@tanstack/react-router';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { ClassificationItem } from '@/lib/api/documents';
import type { DocumentProcessingItem } from '@/lib/api/document-processing';
import { formatDateTime } from '@/lib/utils/format';
import { DocumentProcessingStatus } from './document-processing-status';
import { stageOf } from './processing-logic';

/**
 * T-0099 / C20-137 — Ingestion section for the documents library left rail.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.documents` lines 316-320 + the `ingestionItem()` helper at L360.
 * Each row shows: document name, a short status phrase, and a tiny posture
 * dot (healthy / unassessed / at-risk).
 *
 * Sources (T-0040..T-0044, T-0159):
 *   - Documents list (`useDocumentProcessingQuery`) — the pipeline stage
 *     (uploading / reading / indexing / ready / failed) with real progress,
 *     and on failure the failed stage, reason and retry action.
 *   - Classifications list — has `status` (pending / predicted / confirmed /
 *     corrected / failed), useful for the "routed to N records" / "OCR ..."
 *     phrasing.
 *
 * The section surfaces the four most-recent-not-terminal documents by
 * default. Once everything is `indexed` + `confirmed`, the section collapses
 * to an empty-state "All caught up" — the prototype renders the same
 * positive state on a fully-indexed engagement.
 *
 * T-0159: a document from the documents list shows its pipeline stage with
 * real progress ("Reading 40% · 52 of 128 pages", "Indexing 63% · 2,914 of
 * 4,640 passages"), and on failure the stage, the reason in plain words and
 * Retry (`DocumentProcessingStatus`). Failed documents come first.
 *
 * Backend gaps (Pending):
 *   - The prototype shows ingestion "routed to N records" — this requires
 *     a per-doc proposal count, which the backend doesn't ship on the
 *     documents projection. We render this as a generic "indexed" phrase
 *     until T-0123 (or similar) adds the proposal-count rollup.
 */

export interface IngestionSectionProps {
  engagementId: string;
  /** Items not yet at a terminal classification status. */
  pendingItems: ClassificationItem[];
  /** Documents list — owns `process_status` and the pipeline view (T-0159). */
  documents: DocumentProcessingItem[];
  className?: string;
}

type IngestionPosture = 'healthy' | 'at-risk' | 'unassessed';

/** Status of a document the documents list does not have yet (classifications only). */
function statusFor(cls: ClassificationItem | undefined): {
  label: string;
  posture: IngestionPosture;
  icon: React.ReactNode;
} {
  if (cls) {
    switch (cls.status) {
      case 'pending':
        return {
          label: 'Parsing tables…',
          posture: 'unassessed',
          icon: <Clock className="h-3 w-3" aria-hidden="true" />,
        };
      case 'predicted':
        return {
          label: 'Classified · awaiting confirmation',
          posture: 'unassessed',
          icon: <Clock className="h-3 w-3" aria-hidden="true" />,
        };
      case 'confirmed':
      case 'corrected':
        return {
          label: 'Classified',
          posture: 'healthy',
          icon: <CheckCircle2 className="h-3 w-3" aria-hidden="true" />,
        };
      case 'failed':
        return {
          label: 'Failed',
          posture: 'at-risk',
          icon: <AlertCircle className="h-3 w-3" aria-hidden="true" />,
        };
    }
  }
  return {
    label: 'Pending',
    posture: 'unassessed',
    icon: <Clock className="h-3 w-3" aria-hidden="true" />,
  };
}

const POSTURE_DOT: Record<IngestionPosture, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  unassessed: 'bg-ink-300',
};

export function IngestionSection({
  engagementId,
  pendingItems,
  documents,
  className,
}: IngestionSectionProps) {
  // Build a quick lookup by document_id so we can pair the two projections.
  const docsById = new Map(documents.map((d) => [d.id, d]));
  const clsById = new Map(pendingItems.map((c) => [c.document_id, c]));

  // Compose the visible ingestion-row list. We surface up to four rows so
  // the rail doesn't unbalance the right pane.
  // Strategy:
  //   1. Failed docs first (need attention).
  //   2. In-flight docs (process_status != indexed && != failed).
  //   3. If nothing pending, show the most-recently-indexed docs as healthy.
  const candidates: Array<{
    id: string;
    title: string;
    when: string | null;
  }> = [];
  for (const doc of documents) {
    if (stageOf(doc) === 'failed') {
      candidates.push({ id: doc.id, title: doc.name, when: doc.updated_at });
    }
  }
  for (const doc of documents) {
    const stage = stageOf(doc);
    if (stage !== 'failed' && stage !== 'ready') {
      if (!candidates.some((c) => c.id === doc.id)) {
        candidates.push({ id: doc.id, title: doc.name, when: doc.updated_at });
      }
    }
  }
  // Pull pending classifications that aren't already covered by the docs list.
  for (const cls of pendingItems) {
    if (!candidates.some((c) => c.id === cls.document_id)) {
      candidates.push({
        id: cls.document_id,
        title: cls.document_title,
        when: cls.classified_at ?? null,
      });
    }
  }
  // Fill with recently-indexed docs if the queue is empty.
  if (candidates.length === 0) {
    const recent = [...documents]
      .filter((d) => stageOf(d) === 'ready')
      .sort((a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime())
      .slice(0, 3);
    for (const d of recent) {
      candidates.push({ id: d.id, title: d.name, when: d.updated_at });
    }
  }
  const rows = candidates.slice(0, 4);

  if (rows.length === 0) {
    return (
      <div className={cn('text-meta text-ink-500', className)}>No documents ingested yet.</div>
    );
  }

  return (
    <ul className={cn('flex flex-col gap-1.5', className)}>
      {rows.map((row) => {
        const doc = docsById.get(row.id);
        const status = doc ? null : statusFor(clsById.get(row.id));
        return (
          <li key={row.id} className="rounded-card bg-paper-50 px-2.5 py-2">
            <Link
              to="/engagements/$engagementId/documents/$documentId"
              params={{ engagementId, documentId: row.id }}
              className="block truncate text-meta font-medium text-ink-900 no-underline hover:underline"
              title={row.title}
            >
              {row.title}
            </Link>
            {doc ? (
              <DocumentProcessingStatus
                className="mt-1"
                engagementId={engagementId}
                document={doc}
              />
            ) : status ? (
              <div className="mt-0.5 flex items-center gap-1.5 text-meta text-ink-500">
                <span
                  className={cn(
                    'inline-block h-1.5 w-1.5 flex-none rounded-full',
                    POSTURE_DOT[status.posture],
                  )}
                  aria-hidden="true"
                />
                <span className="inline-flex items-center gap-1">
                  {status.icon}
                  <span>{status.label}</span>
                </span>
              </div>
            ) : null}
            {row.when ? (
              <div className="mt-0.5 font-mono text-meta text-ink-500">
                {formatDateTime(row.when)}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
